'use strict';

/* ============================================================
   When I Grow Up — Mini Cart Drawer (global, all pages)
   ============================================================ */

(function () {

  // ── DOM refs
  const fab      = document.getElementById('wigoCartFab');
  const drawer   = document.getElementById('wigoMiniCart');
  const overlay  = document.getElementById('wigoMcOverlay');
  const closeBtn = document.getElementById('wigoMcClose');
  const body     = document.getElementById('wigoMcBody');
  const footer   = document.getElementById('wigoMcFooter');
  const countEl  = document.getElementById('wigoCartCount');

  if (!fab || !drawer) return; // Not injected on /cart page

  // ── State
  let isOpen = false;
  let isLoading = false;

  // ── Market / currency
  const IS_US = ['US','CA'].includes(window.Shopify?.country);
  let MC_CURRENCY = window.Shopify?.currency?.active || 'GBP';
  function fmt(cents) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: MC_CURRENCY }).format(amount);
    } catch (err) { console.warn('[wigo-mini-cart] currency format failed for', MC_CURRENCY, err); return amount.toFixed(2); }
  }

  // ── Open / close
  function openDrawer() {
    isOpen = true;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    fab.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    loadCart();
  }

  function closeDrawer() {
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    fab.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  fab.addEventListener('click', () => isOpen ? closeDrawer() : openDrawer());
  closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  // ── Cart API
  async function apiGetCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function apiChange(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error('cart change failed');
    return res.json();
  }

  // ── Update count badge
  function updateCount(n) {
    if (!countEl) return;
    countEl.textContent = n;
    countEl.dataset.count = n;
    countEl.setAttribute('aria-label', `${n} item${n === 1 ? '' : 's'} in cart`);
  }

  // ── Escape HTML
  function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c =>
      ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])
    );
  }

  // ── Render cart into drawer
  function renderDrawer(cart) {
    if (cart.currency) MC_CURRENCY = cart.currency;
    updateCount(cart.item_count);

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="wigo-mc-empty">
          <p>Your cart is empty.</p>
          <a href="/" class="wigo-mc-view-cart">← Continue shopping</a>
        </div>`;
      footer.innerHTML = '';
      return;
    }

    body.innerHTML = cart.items.map(item => {
      const imgHtml = item.image
        ? `<img class="wigo-mc-item-img" src="${esc(item.image)}" alt="${esc(item.product_title)}" width="56" height="56">`
        : `<div class="wigo-mc-item-img-fallback">📖</div>`;

      const wasHtml = item.compare_at_price && item.compare_at_price > item.price
        ? `<span class="wigo-mc-price-was">${fmt(item.compare_at_price)}</span>`
        : '';

      return `
        <div class="wigo-mc-item" data-key="${esc(item.key)}">
          ${imgHtml}
          <div class="wigo-mc-item-body">
            <p class="wigo-mc-item-name">${esc(item.product_title)}</p>
            <p class="wigo-mc-item-meta">${IS_US ? 'Free tracked shipping' : 'Tracked UK delivery'}</p>
            <div class="wigo-mc-item-controls">
              <div class="wigo-mc-qty" data-key="${esc(item.key)}">
                <button class="wigo-mc-qty-btn" data-dir="-1" aria-label="Decrease quantity">−</button>
                <span class="wigo-mc-qty-val">${item.quantity}</span>
                <button class="wigo-mc-qty-btn" data-dir="1" aria-label="Increase quantity">+</button>
              </div>
              <button class="wigo-mc-remove" data-key="${esc(item.key)}">Remove</button>
            </div>
          </div>
          <div class="wigo-mc-item-price">
            ${wasHtml}
            <span class="wigo-mc-price-now">${fmt(item.final_line_price)}</span>
          </div>
        </div>`;
    }).join('');

    const discountHtml = cart.total_discount > 0 ? `
      <div class="wigo-mc-summary-row wigo-mc-summary-row--discount">
        <span>2-book bundle saving</span>
        <span>−${fmt(cart.total_discount)}</span>
      </div>` : '';

    const savingsHtml = cart.total_discount > 0 ? `
      <div class="wigo-mc-savings">
        🎉 You're saving ${fmt(cart.total_discount)} on the 2-book bundle
      </div>` : '';

    footer.innerHTML = `
      <div class="wigo-mc-summary-row">
        <span>${cart.item_count} × ${IS_US ? 'Personalized' : 'Personalised'} Book</span>
        <span>${fmt(cart.total_price + cart.total_discount)}</span>
      </div>
      ${discountHtml}
      <div class="wigo-mc-summary-row wigo-mc-summary-row--total">
        <span>Total</span>
        <span>${fmt(cart.total_price)}</span>
      </div>
      ${savingsHtml}
      <a href="/checkout" class="wigo-mc-checkout-btn">🔒 Proceed to checkout</a>
      <a href="/cart" class="wigo-mc-view-cart">View full cart</a>
      <div class="wigo-mc-trust">
        <span>🔒 Secure checkout</span>
        <span>📦 ${IS_US ? 'Free tracked shipping' : 'Tracked UK delivery'}</span>
        <span>💛 Money-back guarantee</span>
      </div>`;
  }

  async function loadCart() {
    if (isLoading) return;
    isLoading = true;
    body.innerHTML = '<div class="wigo-mc-loading">Loading…</div>';
    try {
      const cart = await apiGetCart();
      renderDrawer(cart);
    } catch (err) {
      console.error('[wigo-mini-cart] load failed:', err);
      body.innerHTML = '<div class="wigo-mc-loading">Something went wrong. Please refresh.</div>';
    } finally {
      isLoading = false;
    }
  }

  body.addEventListener('click', async function(e) {
    const qtyBtn = e.target.closest('.wigo-mc-qty-btn');
    if (!qtyBtn) return;
    const qtyEl  = qtyBtn.closest('.wigo-mc-qty');
    const itemEl = qtyBtn.closest('.wigo-mc-item');
    if (!qtyEl || !itemEl) return;
    const key     = qtyEl.dataset.key;
    const valEl   = qtyEl.querySelector('.wigo-mc-qty-val');
    const dir     = parseInt(qtyBtn.dataset.dir, 10);
    const current = parseInt(valEl.textContent, 10);
    const newQty  = Math.max(0, current + dir);
    itemEl.classList.add('is-loading');
    qtyEl.querySelectorAll('.wigo-mc-qty-btn').forEach(b => b.disabled = true);
    try {
      await apiChange(key, newQty);
      const cart = await apiGetCart();
      renderDrawer(cart);
    } catch (err) {
      console.error('[wigo-mini-cart] qty change failed:', err);
      itemEl.classList.remove('is-loading');
    }
  });

  body.addEventListener('click', async function(e) {
    const removeBtn = e.target.closest('.wigo-mc-remove');
    if (!removeBtn) return;
    const key    = removeBtn.dataset.key;
    const itemEl = removeBtn.closest('.wigo-mc-item');
    if (!itemEl) return;
    itemEl.classList.add('is-loading');
    try {
      await apiChange(key, 0);
      const cart = await apiGetCart();
      renderDrawer(cart);
    } catch (err) {
      console.error('[wigo-mini-cart] remove failed:', err);
      itemEl.classList.remove('is-loading');
    }
  });

  (async function initCount() {
    try {
      const cart = await apiGetCart();
      updateCount(cart.item_count);
    } catch (err) {
      console.warn('[wigo-mini-cart] init count failed:', err);
    }
  })();

})();
